import React, { useCallback } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiPlus, mdiClose } from '@mdi/js';
import { OperatingScenario, CircuitNode } from '../types';
import { v4 as uuidv4 } from 'uuid';

/* ────────────────────────── Styled components ────────────────────────── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
`;

const Th = styled.th`
  padding: 6px 10px;
  text-align: left;
  font-weight: 600;
  color: #5f6368;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaed;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const ThScenario = styled.th`
  padding: 4px 6px;
  text-align: center;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaed;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
  min-width: 100px;
`;

const ScenarioHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const NameInput = styled.input`
  border: none;
  background: transparent;
  font-size: 11px;
  font-weight: 600;
  color: #202124;
  padding: 2px 4px;
  text-align: center;
  width: 80px;
  min-width: 0;

  &:focus {
    outline: none;
    border-bottom: 1px solid #1a73e8;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  padding: 1px;
  background: none;
  border: none;
  cursor: pointer;
  color: #9aa0a6;
  border-radius: 2px;
  flex-shrink: 0;

  &:hover {
    color: #d93025;
    background: #fce8e6;
  }
`;

const AddColumnTh = styled.th`
  padding: 4px 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaed;
  position: sticky;
  top: 0;
  z-index: 1;
  width: 36px;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  background: none;
  border: 1px solid #dadce0;
  border-radius: 3px;
  cursor: pointer;
  color: #5f6368;

  &:hover {
    background: #f1f3f4;
    border-color: #bdc1c6;
  }
`;

const Td = styled.td`
  padding: 5px 10px;
  color: #202124;
  border-bottom: 1px solid #f1f3f4;
  white-space: nowrap;
`;

const TdInput = styled.td`
  padding: 3px 6px;
  border-bottom: 1px solid #f1f3f4;
  text-align: center;
`;

const CellInput = styled.input`
  width: 80px;
  padding: 3px 6px;
  border: 1px solid #dadce0;
  border-radius: 3px;
  font-size: 11px;
  color: #202124;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }
`;

const RowLabel = styled.span`
  font-size: 11px;
  color: #5f6368;
`;

const RowUnit = styled.span`
  font-size: 10px;
  color: #9aa0a6;
  margin-left: 4px;
`;

const EmptyAddTd = styled.td`
  border-bottom: 1px solid #f1f3f4;
`;

const EmptyState = styled.div`
  padding: 16px 12px;
  text-align: center;
  font-size: 12px;
  color: #9aa0a6;
`;

/* ────────────────────────── Component ────────────────────────── */

interface ScenariosPanelProps {
  scenarios: OperatingScenario[];
  onChange: (scenarios: OperatingScenario[]) => void;
  nodes: CircuitNode[];
  disabled?: boolean;
}

export const ScenariosPanel: React.FC<ScenariosPanelProps> = ({
  scenarios,
  onChange,
  nodes,
  disabled,
}) => {
  const heatSourceNodes = nodes.filter(
    n => n.data.component?.component_type === 'heat_source'
  );
  const pumpNodes = nodes.filter(
    n => n.data.component?.component_type === 'pump'
  );

  const addScenario = useCallback(() => {
    const newScenario: OperatingScenario = {
      id: uuidv4(),
      name: `Scenario ${scenarios.length + 1}`,
      ambient_temperature: 35,
      component_overrides: {},
    };
    onChange([...scenarios, newScenario]);
  }, [scenarios, onChange]);

  const removeScenario = useCallback((id: string) => {
    onChange(scenarios.filter(s => s.id !== id));
  }, [scenarios, onChange]);

  const updateScenario = useCallback((id: string, updates: Partial<OperatingScenario>) => {
    onChange(
      scenarios.map(s => s.id === id ? { ...s, ...updates } : s)
    );
  }, [scenarios, onChange]);

  const updateOverride = useCallback(
    (scenarioId: string, nodeId: string, field: string, value: number | undefined) => {
      const scenario = scenarios.find(s => s.id === scenarioId);
      if (!scenario) return;

      const overrides = { ...scenario.component_overrides };
      if (value === undefined) {
        const existing = { ...overrides[nodeId] };
        delete (existing as any)[field];
        if (Object.keys(existing).length === 0) {
          delete overrides[nodeId];
        } else {
          overrides[nodeId] = existing;
        }
      } else {
        overrides[nodeId] = { ...overrides[nodeId], [field]: value };
      }

      updateScenario(scenarioId, { component_overrides: overrides });
    },
    [scenarios, updateScenario]
  );

  if (scenarios.length === 0) {
    return (
      <Container>
        <EmptyState>
          No scenarios defined.
          <br />
          <AddButton
            onClick={addScenario}
            disabled={disabled}
            style={{ display: 'inline-flex', gap: 4, marginTop: 8, padding: '4px 12px', fontSize: 11 }}
          >
            <Icon path={mdiPlus} size={0.5} />
            Add Scenario
          </AddButton>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Parameter</Th>
              {scenarios.map((scenario) => (
                <ThScenario key={scenario.id}>
                  <ScenarioHeaderContent>
                    <NameInput
                      value={scenario.name}
                      onChange={(e) => updateScenario(scenario.id, { name: e.target.value })}
                      disabled={disabled}
                    />
                    <RemoveButton
                      onClick={() => removeScenario(scenario.id)}
                      title="Remove scenario"
                      disabled={disabled}
                    >
                      <Icon path={mdiClose} size={0.4} />
                    </RemoveButton>
                  </ScenarioHeaderContent>
                </ThScenario>
              ))}
              <AddColumnTh>
                <AddButton onClick={addScenario} disabled={disabled} title="Add scenario">
                  <Icon path={mdiPlus} size={0.45} />
                </AddButton>
              </AddColumnTh>
            </tr>
          </thead>
          <tbody>
            {/* Ambient temperature row */}
            <tr>
              <Td>
                <RowLabel>Ambient Temp</RowLabel>
                <RowUnit>[°C]</RowUnit>
              </Td>
              {scenarios.map((scenario) => (
                <TdInput key={scenario.id}>
                  <CellInput
                    type="number"
                    value={scenario.ambient_temperature}
                    onChange={(e) =>
                      updateScenario(scenario.id, {
                        ambient_temperature: parseFloat(e.target.value) || 0,
                      })
                    }
                    disabled={disabled}
                  />
                </TdInput>
              ))}
              <EmptyAddTd />
            </tr>

            {/* Heat source override rows */}
            {heatSourceNodes.map((node) => (
              <tr key={node.id}>
                <Td>
                  <RowLabel title={node.data.custom_name || node.data.component.name}>
                    {(node.data.custom_name || node.data.component.name).slice(0, 20)}
                  </RowLabel>
                  <RowUnit>[kW]</RowUnit>
                </Td>
                {scenarios.map((scenario) => (
                  <TdInput key={scenario.id}>
                    <CellInput
                      type="number"
                      step="0.1"
                      placeholder={String(node.data.properties.heat_input_kw || '')}
                      value={scenario.component_overrides[node.id]?.heat_input ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateOverride(
                          scenario.id,
                          node.id,
                          'heat_input',
                          val === '' ? undefined : parseFloat(val)
                        );
                      }}
                      disabled={disabled}
                    />
                  </TdInput>
                ))}
                <EmptyAddTd />
              </tr>
            ))}

            {/* Pump override rows */}
            {pumpNodes.map((node) => (
              <tr key={node.id}>
                <Td>
                  <RowLabel title={node.data.custom_name || node.data.component.name}>
                    {(node.data.custom_name || node.data.component.name).slice(0, 20)}
                  </RowLabel>
                  <RowUnit>[l/min]</RowUnit>
                </Td>
                {scenarios.map((scenario) => (
                  <TdInput key={scenario.id}>
                    <CellInput
                      type="number"
                      step="0.1"
                      placeholder={String(node.data.properties.flow_rate || '')}
                      value={scenario.component_overrides[node.id]?.pump_flow ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateOverride(
                          scenario.id,
                          node.id,
                          'pump_flow',
                          val === '' ? undefined : parseFloat(val)
                        );
                      }}
                      disabled={disabled}
                    />
                  </TdInput>
                ))}
                <EmptyAddTd />
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </Container>
  );
};
